/**
 * UserDropdown - Shared user toolbar/dropdown component
 * Provides consistent toolbar behavior across all dashboard pages:
 * - Dropdown menu with usage, settings, sign out
 * - Tier badge display (ADMIN, ENTERPRISE, PRO, BASIC)
 * - Toolbar entrance animations
 * - Usage modal functionality
 */

import { showCustomModal, showAlert } from './ModalManager';
import { Logger } from '../utils/Logger';
import {
  type UserProfile,
  type UserTier,
  type BandwidthLimitBehavior,
  getStorageLimit,
  getMuxMinutesLimit,
  getMuxMinutesUsed,
  TIER_CONFIG,
  toggleUserAnalytics,
  updateBandwidthLimitBehavior,
} from '../api/UserProfileAPI';
import { formatFileSize } from '../utils/FileUploader';
import { logOut } from '../firebase/auth';
import * as AIAPI from '../api/AIAPI';

const logger = Logger.getLogger('UserDropdown');

// SVG Icons
const ICONS = {
  usage: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"/></svg>`,
  settings: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/></svg>`,
  signOut: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>`,
};

/**
 * Generate the dropdown menu HTML
 */
export function getDropdownMenuHTML(): string {
  return `
    <button class="dropdown-item" id="user-usage-btn">
      ${ICONS.usage}
      Usage
    </button>
    <button class="dropdown-item" id="user-settings-btn">
      ${ICONS.settings}
      Settings
    </button>
    <div class="dropdown-divider"></div>
    <button class="dropdown-item danger" id="user-signout-btn">
      ${ICONS.signOut}
      Sign Out
    </button>
  `;
}

/**
 * Show the usage modal with current user stats
 */
export async function showUsageModal(userProfile: UserProfile): Promise<void> {
  if (!userProfile) {
    showAlert('Profile not loaded. Please refresh the page.', 'Error', '❌');
    return;
  }

  const tierConfig = TIER_CONFIG[userProfile.tier];
  const storageLimit = getStorageLimit(userProfile);
  const storageUsed = userProfile.storageUsed ?? 0;
  const muxLimit = getMuxMinutesLimit(userProfile);
  const muxUsed = getMuxMinutesUsed(userProfile);

  // Bandwidth tracking
  const bandwidthLimit = tierConfig.bandwidthLimit;
  const bandwidthUsed = userProfile.bandwidthUsed ?? 0;

  // Format storage values
  const storageUsedStr = formatFileSize(storageUsed);
  const storageLimitStr = storageLimit === -1 ? 'Unlimited' : (storageLimit === 0 ? 'Not included' : formatFileSize(storageLimit));
  const storagePercent = storageLimit > 0 ? Math.min(100, Math.round((storageUsed / storageLimit) * 100)) : 0;

  // Format bandwidth values
  const bandwidthUsedStr = formatFileSize(bandwidthUsed);
  const bandwidthLimitStr = bandwidthLimit === -1 ? 'Unlimited' : (bandwidthLimit === 0 ? 'Not included' : formatFileSize(bandwidthLimit));
  const bandwidthPercent = bandwidthLimit > 0 ? Math.min(100, Math.round((bandwidthUsed / bandwidthLimit) * 100)) : 0;

  // Format Mux values
  const muxUsedStr = `${Math.round(muxUsed)} min`;
  const muxLimitStr = muxLimit === -1 ? 'Unlimited' : (muxLimit === 0 ? 'Not included' : `${muxLimit} min`);
  const muxPercent = muxLimit > 0 ? Math.min(100, Math.round((muxUsed / muxLimit) * 100)) : 0;

  // Fetch AI usage from backend
  let aiProUsed = 0;
  let aiFlashUsed = 0;
  let aiProLimitVal = tierConfig.aiProLimit;
  let aiFlashLimitVal = tierConfig.aiFlashLimit;

  try {
    const aiUsage = await AIAPI.getAIUsage();
    aiProUsed = aiUsage.pro.used;
    aiFlashUsed = aiUsage.flash.used;
    aiProLimitVal = aiUsage.pro.limit;
    aiFlashLimitVal = aiUsage.flash.limit;
  } catch (error) {
    logger.warn('Could not fetch AI usage:', error);
  }

  const aiProStr = aiProLimitVal === -1 ? `${aiProUsed} / Unlimited` : (aiProLimitVal === 0 ? 'Not included' : `${aiProUsed} / ${aiProLimitVal}`);
  const aiFlashStr = aiFlashLimitVal === -1 ? `${aiFlashUsed} / Unlimited` : (aiFlashLimitVal === 0 ? 'Not included' : `${aiFlashUsed} / ${aiFlashLimitVal}`);
  const aiProPercent = aiProLimitVal > 0 ? Math.min(100, Math.round((aiProUsed / aiProLimitVal) * 100)) : 0;
  const aiFlashPercent = aiFlashLimitVal > 0 ? Math.min(100, Math.round((aiFlashUsed / aiFlashLimitVal) * 100)) : 0;

  const getBarColor = (percent: number) => percent > 90 ? '#ef4444' : percent > 70 ? '#f59e0b' : '#10b981';

  // Calculate overage info for bandwidth
  const bandwidthOverageRate = tierConfig.bandwidthOverageRate; // cents per GB
  const isOverBandwidth = bandwidthLimit > 0 && bandwidthUsed > bandwidthLimit;
  const overageGB = isOverBandwidth ? (bandwidthUsed - bandwidthLimit) / (1024 * 1024 * 1024) : 0;
  const overageCost = overageGB * bandwidthOverageRate / 100; // Convert cents to dollars

  const usageContent = `
    <div style="text-align: left; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
      <div style="margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid #e0e0e0;">
        <div style="font-size: 14px; color: #666; margin-bottom: 4px;">Current Plan</div>
        <div style="font-size: 20px; font-weight: 600; color: #1a1a1a;">${tierConfig.displayName}</div>
      </div>

      <div style="margin-bottom: 20px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
          <span style="font-weight: 500; color: #333;">Cloud Storage</span>
          <span style="color: #666;">${storageUsedStr} / ${storageLimitStr}</span>
        </div>
        ${storageLimit > 0 ? `
        <div style="background: #e5e7eb; border-radius: 4px; height: 8px; overflow: hidden;">
          <div style="background: ${getBarColor(storagePercent)}; height: 100%; width: ${storagePercent}%; transition: width 0.3s;"></div>
        </div>
        ` : ''}
      </div>

      <div style="margin-bottom: 20px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
          <span style="font-weight: 500; color: #333;">Monthly Bandwidth</span>
          <span style="color: #666;">${bandwidthUsedStr} / ${bandwidthLimitStr}</span>
        </div>
        ${bandwidthLimit > 0 ? `
        <div style="background: #e5e7eb; border-radius: 4px; height: 8px; overflow: hidden;">
          <div style="background: ${getBarColor(bandwidthPercent)}; height: 100%; width: ${Math.min(bandwidthPercent, 100)}%; transition: width 0.3s;"></div>
        </div>
        ${isOverBandwidth ? `
        <div style="font-size: 12px; color: #ef4444; margin-top: 4px;">
          ⚠️ Over limit by ${formatFileSize(bandwidthUsed - bandwidthLimit)} (est. overage: $${overageCost.toFixed(2)})
        </div>
        ` : ''}
        ` : ''}
      </div>

      <div style="margin-bottom: 20px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
          <span style="font-weight: 500; color: #333;">HLS Video Minutes</span>
          <span style="color: #666;">${muxUsedStr} / ${muxLimitStr}</span>
        </div>
        ${muxLimit > 0 ? `
        <div style="background: #e5e7eb; border-radius: 4px; height: 8px; overflow: hidden;">
          <div style="background: ${getBarColor(muxPercent)}; height: 100%; width: ${muxPercent}%; transition: width 0.3s;"></div>
        </div>
        ` : ''}
      </div>

      <div style="margin-bottom: 20px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
          <span style="font-weight: 500; color: #333;">AI Pro Generations</span>
          <span style="color: #666;">${aiProStr}</span>
        </div>
        ${aiProLimitVal > 0 ? `
        <div style="background: #e5e7eb; border-radius: 4px; height: 8px; overflow: hidden;">
          <div style="background: ${getBarColor(aiProPercent)}; height: 100%; width: ${aiProPercent}%; transition: width 0.3s;"></div>
        </div>
        ` : ''}
      </div>

      <div style="margin-bottom: 20px;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
          <span style="font-weight: 500; color: #333;">AI Flash Generations</span>
          <span style="color: #666;">${aiFlashStr}</span>
        </div>
        ${aiFlashLimitVal > 0 ? `
        <div style="background: #e5e7eb; border-radius: 4px; height: 8px; overflow: hidden;">
          <div style="background: ${getBarColor(aiFlashPercent)}; height: 100%; width: ${aiFlashPercent}%; transition: width 0.3s;"></div>
        </div>
        ` : ''}
      </div>

      <div style="margin-top: 24px; padding-top: 16px; border-top: 1px solid #e0e0e0; font-size: 13px; color: #888;">
        Note: Bandwidth resets monthly. AI usage also resets monthly. Storage tracking started recently and may not reflect files uploaded before this feature.
      </div>
    </div>
  `;

  showCustomModal({
    title: 'Account Usage',
    icon: '📊',
    customContent: usageContent,
    confirmText: 'Close',
    showCancel: false,
  });
}

/**
 * Show the settings modal - shared across all pages
 */
export async function showSettingsModal(userProfile: UserProfile, userId: string): Promise<void> {
  if (!userProfile) {
    showAlert('Profile not loaded. Please refresh the page.', 'Error', '❌');
    return;
  }

  const analyticsEnabled = userProfile.analyticsEnabled !== false;
  const bandwidthBehavior = userProfile.bandwidthLimitBehavior || 'soft';
  const tierConfig = TIER_CONFIG[userProfile.tier];
  const hasBandwidthLimit = tierConfig.bandwidthLimit > 0;

  const settingsContent = `
    <div style="text-align: left; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
      <div style="margin-bottom: 24px;">
        <div style="font-weight: 600; color: #333; margin-bottom: 12px;">Analytics & Privacy</div>
        <label style="display: flex; align-items: center; gap: 10px; cursor: pointer;">
          <input type="checkbox" id="settings-analytics-checkbox" ${analyticsEnabled ? 'checked' : ''}
            style="width: 18px; height: 18px; cursor: pointer;">
          <span style="color: #555;">Enable analytics for all my sequences</span>
        </label>
        <div style="font-size: 13px; color: #888; margin-top: 8px; padding-left: 28px;">
          When enabled, we collect anonymous usage data to help improve your experiences.
        </div>
      </div>

      ${hasBandwidthLimit ? `
      <div style="margin-bottom: 24px; padding-top: 16px; border-top: 1px solid #e0e0e0;">
        <div style="font-weight: 600; color: #333; margin-bottom: 12px;">Bandwidth Limit Behavior</div>
        <div style="font-size: 13px; color: #666; margin-bottom: 12px;">
          Choose what happens when your tours exceed your monthly bandwidth limit (${formatFileSize(tierConfig.bandwidthLimit)}):
        </div>

        <label style="display: flex; align-items: flex-start; gap: 10px; cursor: pointer; margin-bottom: 12px;">
          <input type="radio" name="bandwidth-behavior" value="soft" ${bandwidthBehavior === 'soft' ? 'checked' : ''}
            style="width: 18px; height: 18px; cursor: pointer; margin-top: 2px;">
          <div>
            <span style="color: #555; font-weight: 500;">Allow overages (recommended)</span>
            <div style="font-size: 12px; color: #888; margin-top: 4px;">
              Tours keep working. Extra bandwidth is billed at $${(tierConfig.bandwidthOverageRate / 100).toFixed(2)}/GB at end of month.
            </div>
          </div>
        </label>

        <label style="display: flex; align-items: flex-start; gap: 10px; cursor: pointer;">
          <input type="radio" name="bandwidth-behavior" value="hard" ${bandwidthBehavior === 'hard' ? 'checked' : ''}
            style="width: 18px; height: 18px; cursor: pointer; margin-top: 2px;">
          <div>
            <span style="color: #555; font-weight: 500;">Block when exceeded</span>
            <div style="font-size: 12px; color: #888; margin-top: 4px;">
              Tours stop loading when limit is reached. No overage charges. Resets next billing cycle.
            </div>
          </div>
        </label>
      </div>
      ` : ''}
    </div>
  `;

  return new Promise((resolve) => {
    showCustomModal({
      title: 'Settings',
      icon: '⚙️',
      customContent: settingsContent,
      confirmText: 'Save Settings',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: async () => {
        try {
          // Save analytics setting
          const analyticsCheckbox = document.getElementById('settings-analytics-checkbox') as HTMLInputElement;
          if (analyticsCheckbox) {
            const newAnalyticsValue = analyticsCheckbox.checked;
            if (newAnalyticsValue !== (userProfile.analyticsEnabled !== false)) {
              await toggleUserAnalytics(userId, newAnalyticsValue);
              userProfile.analyticsEnabled = newAnalyticsValue;
            }
          }

          // Save bandwidth behavior setting (only if user has bandwidth limits)
          if (hasBandwidthLimit) {
            const selectedBehavior = (document.querySelector('input[name="bandwidth-behavior"]:checked') as HTMLInputElement)?.value as BandwidthLimitBehavior;
            if (selectedBehavior && selectedBehavior !== bandwidthBehavior) {
              await updateBandwidthLimitBehavior(userId, selectedBehavior);
              userProfile.bandwidthLimitBehavior = selectedBehavior;
            }
          }

          showAlert('Settings saved successfully.', 'Settings Saved', '✅');
        } catch (error) {
          logger.error('Failed to save settings:', error);
          showAlert('Failed to save settings. Please try again.', 'Error', '❌');
        }
        resolve();
      },
      onCancel: () => {
        resolve();
      },
    });
  });
}

export interface InitDropdownOptions {
  userProfile: UserProfile | null;
  onSignOut: () => void;
  onSettings?: () => void;
}

/**
 * Initialize the user dropdown with event handlers
 * Call this after the page loads and user profile is available
 */
export function initUserDropdown(options: InitDropdownOptions): void {
  const dropdownMenu = document.querySelector('.dropdown-menu');

  if (dropdownMenu) {
    // Replace dropdown content with standardized HTML
    dropdownMenu.innerHTML = getDropdownMenuHTML();
  }

  // Usage button handler
  const usageBtn = document.getElementById('user-usage-btn');
  if (usageBtn) {
    usageBtn.addEventListener('click', () => {
      if (options.userProfile) {
        showUsageModal(options.userProfile);
      } else {
        showAlert('Profile not loaded yet. Please wait or refresh.', 'Not Ready', '⏳');
      }
    });
  }

  // Settings button handler
  const settingsBtn = document.getElementById('user-settings-btn');
  if (settingsBtn) {
    settingsBtn.addEventListener('click', () => {
      if (options.onSettings) {
        options.onSettings();
      } else {
        // Default: redirect to analytics page (where settings are)
        window.location.href = '/dashboard/analytics.html';
      }
    });
  }

  // Sign out button handler
  const signOutBtn = document.getElementById('user-signout-btn');
  if (signOutBtn) {
    signOutBtn.addEventListener('click', () => {
      options.onSignOut();
    });
  }
}

/**
 * Update the dropdown when user profile changes
 */
export function updateDropdownProfile(userProfile: UserProfile | null): void {
  // Store reference for usage modal
  if (typeof window !== 'undefined') {
    window.__userDropdownProfile = userProfile;
  }
}

// ============================================================================
// Tier Badge Functions
// ============================================================================

/**
 * Helper to set badge tier class and text
 */
export function setBadgeTier(proBadge: HTMLElement | null, tier: string, text: string): void {
  if (!proBadge) return;

  // Remove all tier classes
  proBadge.classList.remove('tier-admin', 'tier-enterprise', 'tier-pro', 'tier-basic');

  // Add the appropriate tier class
  proBadge.classList.add(`tier-${tier}`);
  proBadge.textContent = text;
  proBadge.style.display = 'inline-flex';
}

/**
 * Update Pro badge visibility based on user tier
 * Unified logic - shows actual tier name (ADMIN, ENTERPRISE, PRO, BASIC)
 */
export function updateProBadge(
  userProfile: UserProfile | null,
  upgradeBtn: HTMLElement | null,
  proBadge: HTMLElement | null
): void {
  if (!upgradeBtn || !proBadge) return;

  const userTier: UserTier = userProfile?.tier ?? 'free';

  // Admin tier
  if (userTier === 'admin') {
    upgradeBtn.style.display = 'none';
    setBadgeTier(proBadge, 'admin', 'ADMIN');
    return;
  }

  // Enterprise tier
  if (userTier === 'enterprise') {
    upgradeBtn.style.display = 'none';
    setBadgeTier(proBadge, 'enterprise', 'ENTERPRISE');
    return;
  }

  // Pro tier
  if (userTier === 'pro') {
    upgradeBtn.style.display = 'none';
    setBadgeTier(proBadge, 'pro', 'PRO');
    return;
  }

  // Basic tier - show badge but also upgrade button
  if (userTier === 'basic') {
    upgradeBtn.style.display = 'inline-block';
    setBadgeTier(proBadge, 'basic', 'BASIC');
    return;
  }

  // Free tier: show upgrade button, hide badge
  upgradeBtn.style.display = 'inline-block';
  proBadge.style.display = 'none';
}

// ============================================================================
// Toolbar Animation Functions
// ============================================================================

/**
 * Trigger toolbar entrance animations
 * Call after init() completes so pro badge/upgrade button state is finalized
 * @param animationClass - The CSS class to look for (e.g., 'editor-toolbar-animate')
 */
export function triggerToolbarAnimations(animationClass: string): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.querySelectorAll(`.${animationClass}`).forEach((el) => {
        el.classList.add('toolbar-ready');
      });
    });
  });
}

// ============================================================================
// Dropdown Toggle Functions
// ============================================================================

/**
 * Setup the dropdown toggle behavior
 */
export function setupDropdownToggle(): void {
  const userDropdown = document.getElementById('user-dropdown');
  const userDropdownToggle = document.getElementById('user-dropdown-toggle');

  if (userDropdownToggle && userDropdown) {
    userDropdownToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      userDropdown.classList.toggle('active');
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!userDropdown.contains(e.target as Node)) {
        userDropdown.classList.remove('active');
      }
    });
  }
}

// ============================================================================
// Comprehensive Toolbar Initialization
// ============================================================================

export interface ToolbarConfig {
  /** The user profile (can be null initially) */
  userProfile: UserProfile | null;
  /** The user ID (required for saving settings) */
  userId: string;
  /** CSS class for toolbar animation (e.g., 'editor-toolbar-animate') */
  animationClass?: string;
  /** Custom settings handler (default: opens shared settings modal) */
  onSettings?: () => void;
  /** Custom sign out handler (default: calls logOut and redirects to auth) */
  onSignOut?: () => Promise<void>;
  /** Whether to trigger animations immediately (default: true) */
  triggerAnimations?: boolean;
}

// Store the current profile and userId for modal updates
let currentToolbarProfile: UserProfile | null = null;
let currentToolbarUserId: string = '';

/**
 * Initialize the complete toolbar with all handlers
 * This is the main entry point for toolbar setup on any page
 */
export function initToolbar(config: ToolbarConfig): void {
  currentToolbarProfile = config.userProfile;
  currentToolbarUserId = config.userId;

  // Setup dropdown toggle behavior
  setupDropdownToggle();

  // Usage button handler
  const usageBtn = document.getElementById('user-usage-btn');
  if (usageBtn) {
    usageBtn.addEventListener('click', async () => {
      if (currentToolbarProfile) {
        await showUsageModal(currentToolbarProfile);
      } else {
        logger.warn('Usage button clicked but no profile available');
        await showAlert('Profile not loaded yet. Please wait.', 'Not Ready', '⏳');
      }
    });
  }

  // Settings button handler
  const settingsBtn = document.getElementById('user-settings-btn');
  if (settingsBtn) {
    settingsBtn.addEventListener('click', async () => {
      if (config.onSettings) {
        config.onSettings();
      } else if (currentToolbarProfile && currentToolbarUserId) {
        await showSettingsModal(currentToolbarProfile, currentToolbarUserId);
      } else {
        logger.warn('Settings button clicked but no profile/userId available');
        await showAlert('Profile not loaded yet. Please wait.', 'Not Ready', '⏳');
      }
    });
  }

  // Sign out button handler
  const signOutBtn = document.getElementById('user-signout-btn');
  if (signOutBtn) {
    signOutBtn.addEventListener('click', async () => {
      if (config.onSignOut) {
        await config.onSignOut();
      } else {
        // Default sign out behavior
        try {
          await logOut();
          window.location.href = '/dashboard/auth.html';
        } catch (error) {
          logger.error('Logout error:', error);
          alert('Failed to sign out');
        }
      }
    });
  }

  // Update tier badge (support both ID conventions)
  const upgradeBtn = document.getElementById('upgrade-btn') ?? document.getElementById('upgradeBtn') as HTMLElement | null;
  const proBadge = document.getElementById('pro-badge') as HTMLElement | null;
  updateProBadge(config.userProfile, upgradeBtn, proBadge);

  // Trigger animations if requested
  if (config.triggerAnimations !== false && config.animationClass) {
    triggerToolbarAnimations(config.animationClass);
  }
}

/**
 * Update the toolbar when user profile changes
 * Call this after profile loads or updates
 */
export function updateToolbarProfile(userProfile: UserProfile | null): void {
  currentToolbarProfile = userProfile;

  // Update tier badge (support both ID conventions)
  const upgradeBtn = document.getElementById('upgrade-btn') ?? document.getElementById('upgradeBtn') as HTMLElement | null;
  const proBadge = document.getElementById('pro-badge') as HTMLElement | null;
  updateProBadge(userProfile, upgradeBtn, proBadge);
}
